import type { PublicUserProfile } from "@fitties/shared";
import { motion } from "framer-motion";

/**
 * Props interface for the NewMatchBubble component.
 */
interface NewMatchBubbleProps {
  /** User data for the match */
  user: PublicUserProfile;
  /** Whether this bubble is currently active */
  isActive: boolean;
  /** Callback when bubble is clicked */
  onClick: () => void;
  /** Whether this is a new match (for animation) */
  isNew?: boolean;
}

/**
 * NewMatchBubble component for displaying new matches in horizontal scroll
 * Perfect circles with 2px border in #b99de1
 *
 * @example
 * ```tsx
 * <NewMatchBubble
 *   user={matchUser}
 *   isActive={true}
 *   onClick={() => handleBubbleClick()}
 *   onStartChat={(userId) => startChat(userId)}
 *   isNew={true}
 * />
 * ```
 */
export function NewMatchBubble({ user, isActive, onClick, isNew = false }: NewMatchBubbleProps) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      className={`relative flex-shrink-0 w-16 h-16 rounded-full border-2 overflow-hidden bg-white shadow-sm ${
        isActive ? "border-green-500" : "border-lavender-400"
      }`}
      whileTap={{ scale: 0.95 }}
      initial={isNew ? { opacity: 0, scale: 0.8 } : { opacity: 1, scale: 1 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 25,
        delay: isNew ? 0.2 : 0,
      }}
      data-testid="new-match-bubble"
      aria-label={`New match with ${user.display_name}${isActive ? " (active)" : ""}`}
    >
      {user.photo_url ? (
        <img src={user.photo_url} alt={`${user.display_name}'s profile`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gray-300 flex items-center justify-center">
          <span className="text-gray-600 text-xs font-medium">{user.display_name.charAt(0).toUpperCase()}</span>
        </div>
      )}

      {/* Active status indicator */}
      {isActive && (
        <motion.div
          className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 400 }}
          data-testid="active-indicator"
          aria-label="User is active"
        />
      )}

      {/* New match indicator */}
      {isNew && (
        <motion.div
          className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 rounded-full border-2 border-white"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, type: "spring", stiffness: 400 }}
          data-testid="new-match-indicator"
        />
      )}
    </motion.button>
  );
}
